import { useEffect, useRef } from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';

/**
 * BANDEAU ÉPHÉMÈRE DE RETOUR D'ACTION, avec « Annuler » optionnel.
 *
 * Né du tri au clavier dans la visionneuse : mettre une photo à la poubelle y est
 * IMMÉDIAT (aucune modale de confirmation — sur 13 500 photos, confirmer chaque geste
 * coûterait plus cher que de se tromper). Le filet n'est donc pas AVANT l'action mais
 * APRÈS : ce bandeau dit ce qui vient de se passer et offre de le défaire.
 *
 * Deux modes, selon la présence de `undo` :
 *  - CONFIRMATION ANNULABLE (`undo` fourni) — `role="status"`, jamais `alert` : une
 *    action réussie que l'utilisateur vient de déclencher n'est pas une alarme.
 *  - ERREUR (`undo` absent) — `role="alert"` + icône : le sens ne passe JAMAIS par la
 *    seule couleur (WCAG 1.4.1).
 *
 * DISPARITION AUTOMATIQUE au bout de `timeoutMs`. Le minuteur est armé UNE fois au
 * montage (le rappel de fermeture est lu par ref) : l'appelant monte un bandeau par
 * notification — typiquement avec une `key` — et chacune obtient son plein délai, même
 * si le parent se redessine dix fois entre-temps.
 *
 * Le délai court ne perd rien : la mise à la poubelle est un soft-delete, et la
 * CORBEILLE reste le recours durable une fois le bandeau parti (ce bandeau n'est qu'un
 * raccourci de confort, pas le seul chemin de restauration).
 */

/** Délai de disparition automatique par défaut (ms) — le temps de lire et réagir. */
const DEFAULT_TIMEOUT_MS = 8000;

interface UndoBannerProps {
  /** Message déjà localisé et interpolé (p. ex. « photo.jpg à la corbeille »). */
  message: string;
  /** Action de retour en arrière ; ABSENTE ⇒ le bandeau est un message d'ERREUR. */
  undo?: { label: string; onUndo: () => void };
  /** Fermeture (échéance atteinte, ou « Annuler » activé). */
  onDismiss: () => void;
  /** Durée d'affichage (ms). Défaut `DEFAULT_TIMEOUT_MS`. */
  timeoutMs?: number;
}

export function UndoBanner({
  message,
  undo,
  onDismiss,
  timeoutMs = DEFAULT_TIMEOUT_MS,
}: UndoBannerProps): React.JSX.Element {
  const dismissRef = useRef(onDismiss);
  dismissRef.current = onDismiss;

  useEffect(() => {
    const timer = setTimeout(() => dismissRef.current(), timeoutMs);
    return () => clearTimeout(timer);
  }, [timeoutMs]);

  return (
    <div
      role={undo ? 'status' : 'alert'}
      className="pointer-events-auto flex w-fit max-w-full items-center gap-3 rounded-md bg-black/80 px-3 py-2 text-sm text-white shadow-lg"
    >
      {undo === undefined && (
        <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-danger" aria-hidden="true" />
      )}
      <span className="min-w-0 truncate">{message}</span>
      {undo && (
        <button
          type="button"
          onClick={() => {
            undo.onUndo();
            dismissRef.current();
          }}
          className="min-h-11 shrink-0 rounded-md px-3 font-medium underline underline-offset-2 hover:bg-white/15 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
        >
          {undo.label}
        </button>
      )}
    </div>
  );
}
